import { Md5 } from 'ts-md5';
import { ref } from 'vue';
import { getConfigValue } from '@/core/consts/config';
import { type BitrixWidgetInstance } from '@/core/types/dom.d';
import { loadScriptPromise } from '@/core/utils/script';
import { useAuthStore } from '@/parts/auth/store/auth';

const isInit = ref(false);
const isLoading = ref(false);
const widgetInstance = ref<BitrixWidgetInstance | null>(null);

export const useBitrixWidget = () => {
  const { profile } = useAuthStore();

  const init = async () => {
    if (isInit.value || isLoading.value) return;

    if (!getConfigValue('bitrixWidgetEnabled')) {
      console.info('Bitrix widget is disabled in config!');
      return;
    }

    if (!getConfigValue('bitrixWidgetUrl')) {
      console.error('Bitrix widget URL is not set');
      return;
    }

    window.addEventListener('b24:form:init', function (event) {
      if (!profile.value?.profile) return;

      const form = event.detail.object;

      form.setValues({
        // предзаполняем форму
        name: profile.value.profile.name,
        'last-name': profile.value.profile.lastName,
        phone: profile.value.profile.phoneNumber,
      });

      // указываем параметры для прокидывания в сделку
      form.setProperty('dealLastName', profile.value.profile.lastName);
      form.setProperty('dealName', profile.value.profile.name);
      form.setProperty('dealSecondName', profile.value.profile.middleName || '');
      form.setProperty('dealEmail', profile.value.email);
      form.setProperty('extranetId', profile.value.supplierCode || '');
      form.setProperty('dealPhone', profile.value.profile.phoneNumber);
      form.setProperty('originalEmail', profile.value.email);
      disableEmojis();
    });

    window.addEventListener('onBitrixLiveChat', function (event) {
      if (!profile.value?.profile) return;

      const widget = event.detail.widget;

      // Установка внешней авторизации пользователя
      widget.setUserRegisterData({
        hash: Md5.hashStr(profile.value.id),
        name: profile.value.profile.name,
        lastName: profile.value.profile.lastName,
        email: `${getConfigValue('bitrixWidgetEmailPrefix') || ''}${profile.value.email}`,
      });

      widget.setCustomData([
        {
          ENTITY_TYPE: 'DEAL',
          ENTITY_ID: 0,
          GRID: [
            {
              NAME: 'Телефон',
              VALUE: profile.value.profile.phoneNumber,
              DISPLAY: 'LINE',
            },
            {
              NAME: 'Email',
              VALUE: profile.value.email || '',
              DISPLAY: 'LINE',
            },
          ],
        },
      ]);

      widget.subscribe({
        type: BX.LiveChatWidget.SubscriptionType.operatorMessage,
        callback: function () {
          disableEmojis();
        },
      });

      widgetInstance.value = widget;
    });

    // удаляем смайлы, т.к. не работают
    const disableEmojis = () => {
      const smileContainer = document.querySelector('.bx-im-textarea-app-button.bx-im-textarea-app-smile');
      if (smileContainer != undefined) {
        smileContainer.remove();
      }
    };

    isLoading.value = true;

    await loadScriptPromise(getConfigValue('bitrixWidgetUrl'));

    isInit.value = true;
    isLoading.value = false;
  };

  const openChat = () => {
    if (!widgetInstance.value) {
      console.error('Widget instance is not available');
      return;
    }

    widgetInstance.value?.open();
  };

  return {
    init,
    openChat,
    widgetInstance,
  };
};
